import "./EmojiReactions.scss";

import { Flex } from "@fluentui/react-northstar";
import { EmojiReaction, EmojiReactionType } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { memo, useRef, useState } from "react";

import { useClickAway } from "../actions/useClickAway";
import { useUserInfo } from "../actions/useUserInfo";
import { Button } from "./Button";
import { PollyIcon } from "./PollyIcon";

export const commentReactions: EmojiReactionType[] = [
  ":laughing:",
  ":thumbsup:",
  ":heart:",
  ":cry:",
  ":clap:",
];

export const emojiMap: { [key in EmojiReactionType]: string } = {
  ":clap:": "👏",
  ":thumbsup:": "👍",
  ":laughing:": "😆",
  ":heart:": "❤️",
  ":cry:": "😢",
};

export const EmojiReactions: React.FC<{
  survey: SurveyDTO;
  reactions: EmojiReaction[];
  size?: "small" | "medium";
  onReactionChange?: (reaction: EmojiReactionType) => void;
}> = memo(({ survey, reactions, size, onReactionChange }) => {
  size = size ?? "medium";
  return (
    <Flex>
      {survey.state === "open" && onReactionChange && (
        <AddEmojiReactions onReactionChange={onReactionChange} />
      )}
      <EmojiReactionList
        size={size}
        isLive={survey.state === "open"}
        reactions={reactions}
        onReactionChange={onReactionChange}
      />
    </Flex>
  );
});

export const EmojiReactionList: React.FC<{
  isLive: boolean;
  reactions: EmojiReaction[];
  size: "small" | "medium";
  onReactionChange?: (reaction: EmojiReactionType) => void;
}> = memo(({ isLive, reactions, size, onReactionChange }) => {
  const { user } = useUserInfo();

  type ReactionListCount = {
    count: number;
    isUserReaction: boolean;
  };
  const reactionList: Record<EmojiReactionType, ReactionListCount> = {} as Record<
    EmojiReactionType,
    ReactionListCount
  >;

  reactions.forEach(reaction => {
    const reactionCount = reactionList[reaction.reaction]?.count || 0;
    const isUserReaction = reactionList[reaction.reaction]?.isUserReaction;
    reactionList[reaction.reaction] = {
      count: reactionCount + 1,
      isUserReaction: isUserReaction || reaction.user.aadObjectId === user.userId,
    };
  });

  const usedEmojis = Object.keys(reactionList) as EmojiReactionType[];

  return (
    <div className="emoji-reactions__reaction-list">
      {usedEmojis.map((key: EmojiReactionType) => {
        const { count, isUserReaction } = reactionList[key];

        const buttonClasses = ["emoji-reactions__reaction-list__item"];
        if (isUserReaction) buttonClasses.push("emoji-reactions__reaction-list__item--selected");
        if (isLive && onReactionChange)
          buttonClasses.push("emoji-reactions__reaction-list__item--active");
        if (size === "small") buttonClasses.push("emoji-reactions__reaction-list__item--small");

        return (
          <Flex
            className={buttonClasses.join(" ")}
            key={key}
            onClick={() => isLive && onReactionChange && onReactionChange(key)}
            gap="gap.smaller"
          >
            <span className="emoji-reactions__reaction-list__emoji">{emojiMap[key]}</span>
            {count > 1 && (
              <span className="emoji-reactions__reaction-list__count">
                {limitDisplayCount(reactionList[key].count)}
              </span>
            )}
          </Flex>
        );
      })}
    </div>
  );
});

export const AddEmojiReactions: React.FC<{
  onReactionChange: (reaction: EmojiReactionType) => void;
}> = ({ onReactionChange }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState(false);

  useClickAway(ref, () => setOpenMenu(false));

  const handleReactionChange = (reaction: EmojiReactionType) => {
    onReactionChange(reaction);
    setOpenMenu(false);
  };

  return (
    <div ref={ref} className="emoji-reactions__add-reaction">
      <Button
        iconOnly
        circular
        icon={<PollyIcon icon="emoji-reaction" />}
        aria-haspopup="true"
        aria-expanded={openMenu}
        onClick={() => {
          setOpenMenu(!openMenu);
        }}
      />

      <div
        className={`emoji-reactions__add-reaction-menu ${
          openMenu ? "emoji-reactions__add-reaction-menu--open" : ""
        }`}
      >
        {commentReactions.map(reaction => {
          return (
            <div
              key={reaction}
              className="emoji-reactions__add-reaction-menu__emoji"
              onClick={() => handleReactionChange(reaction)}
            >
              {emojiMap[reaction]}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const limitDisplayCount = (count: number, limit: number = 99): string => {
  if (count === 1) return "";
  if (count > limit) return `${limit}+`;
  return count.toString();
};
